import { useMemo } from 'react';
import { CartesianGrid, Dot, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { MOOD_META, formatRelativeDay, isoDate } from '../utils';

export default function HistoryTab({ moods }) {
  const sorted = useMemo(
    () => moods.slice().sort((a, b) => (a.date || '').localeCompare(b.date || '')),
    [moods]
  );

  const chartData = useMemo(() => {
    if (sorted.length < 3) return null;
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - 30);
    return sorted
      .filter((m) => new Date(m.date) >= cutoff)
      .map((m) => ({
        date: isoDate(new Date(m.date)),
        score: m.score,
        label: new Date(m.date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
      }));
  }, [sorted]);

  const reversed = useMemo(() => sorted.slice().reverse(), [sorted]);

  return (
    <div className="history-tab">
      <h3>History</h3>
      {chartData ? (
        <div
          className="history-tab__chart"
          role="img"
          aria-label={`Mood trend chart, last 30 days, ${chartData.length} entries. The same entries are listed below.`}
        >
          <ResponsiveContainer width="100%" height={180}>
            <LineChart data={chartData} margin={{ top: 12, right: 8, bottom: 0, left: -24 }}>
              <CartesianGrid stroke="#dcd5c5" strokeDasharray="3 3" vertical={false} />
              <XAxis
                dataKey="label"
                tick={{ fontSize: 11, fill: '#72675c' }}
                axisLine={false}
                tickLine={false}
                interval="preserveStartEnd"
              />
              <YAxis
                domain={[1, 5]}
                ticks={[1, 2, 3, 4, 5]}
                tick={{ fontSize: 11, fill: '#72675c' }}
                axisLine={false}
                tickLine={false}
              />
              <Tooltip
                contentStyle={{
                  background: '#faf7f2',
                  border: '1px solid #c0b8ae',
                  borderRadius: 8,
                  fontFamily: 'Lora, serif'
                }}
                formatter={(v) => [`${MOOD_META[v]?.emoji || ''} ${MOOD_META[v]?.label || v}`, 'mood']}
              />
              <Line
                type="monotone"
                dataKey="score"
                stroke="#7c9e7a"
                strokeWidth={2}
                dot={(props) => {
                  const { cx, cy, payload } = props;
                  return <Dot cx={cx} cy={cy} r={5} fill={MOOD_META[payload.score]?.color || '#7c9e7a'} stroke="none" />;
                }}
                activeDot={{ r: 7 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <p className="muted">Log a few moods and a chart shows up here.</p>
      )}
      <ul className="history-list">
        {reversed.map((m) => (
          <li key={m.id || m.date} className="history-row">
            <span
              className="history-row__emoji"
              role="img"
              aria-label={MOOD_META[m.score]?.label}
              title={MOOD_META[m.score]?.label}
            >
              {MOOD_META[m.score]?.emoji}
            </span>
            <div className="history-row__body">
              <span className="history-row__date">{formatRelativeDay(m.date)}</span>
              {m.note && <span className="history-row__note">{m.note}</span>}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
